<template>
  <div class="proofreading-select-manuscript">
    <h1 class="proofreading-select-manuscript__heading">
      {{ $t("proofreadingSelectManuscriptPage.heading") }}
    </h1>

    <div v-if="isLoadingManuscripts || isLoadingCounts" class="proofreading-select-manuscript__loading">
      <LoadingSpinner size="small" variant="inline" :text="$t('proofreadingSelectManuscriptPage.loadingCounts')" />
    </div>

    <p v-else-if="!hasManuscripts" class="proofreading-select-manuscript__empty">
      {{ $t("proofreadingSelectManuscriptPage.empty") }}
    </p>

    <div v-else class="proofreading-select-manuscript__selector">
      <ExportManuscriptListSelector
        :manuscripts="manuscriptList"
        :selected-manuscripts.sync="selectedManuscripts"
        :character-counts-by-key="isAiMode ? characterCountsByKey : undefined"
        :character-count-unit="isAiMode ? 'character' : 'fish'"
      />
    </div>

    <div v-if="hasCountLoadFailure" class="proofreading-select-manuscript__fish-info">
      <div class="fish-warning">
        <div class="fish-warning-icon">⚠️</div>
        <div class="fish-warning-content">
          <p class="fish-warning-message">{{ $t("proofreadingSelectManuscriptPage.countLoadFailed") }}</p>
        </div>
      </div>
    </div>

    <div v-else-if="isAiMode && selectedManuscripts.length > 0" class="proofreading-select-manuscript__fish-info">
      <div class="fish-info-content">
        <i18n path="proofreadingSelectManuscriptPage.aiCharCount" tag="span">
          <strong place="amount" class="fish-info-amount">{{ formattedTotalChars }}</strong>
        </i18n>
      </div>
      <div v-if="showFishWarning" class="fish-warning">
        <div class="fish-warning-icon">⚠️</div>
        <div class="fish-warning-content">
          <p class="fish-warning-message">{{ fishWarningMessage }}</p>
          <p v-if="isFishInsufficient" class="fish-warning-detail">
            <i18n path="proofreadingSelectManuscriptPage.fishShortageDetail" tag="span">
              <strong place="amount" class="fish-shortage-amount">{{ formattedFishShortage }}</strong>
            </i18n>
          </p>
        </div>
      </div>
    </div>

    <div class="proofreading-select-manuscript__footer">
      <button
        type="button"
        class="proofreading-select-manuscript__back-button"
        @click="onClickBack"
      >
        {{ $t("proofreadingSelectManuscriptPage.back") }}
      </button>
      <button
        type="button"
        class="proofreading-select-manuscript__next-button"
        :disabled="!canProceed"
        @click="onClickNext"
      >
        {{ $t("proofreadingSelectManuscriptPage.next") }}
      </button>
    </div>

    <div v-if="isAiMode" class="proofreading-select-manuscript__notice">
      <p class="notice-text">{{ $t("proofreadingSelectManuscriptPage.aiNoticeMaxChars") }}</p>
      <p class="notice-text">{{ $t("proofreadingSelectManuscriptPage.aiNoticeSelectOrder") }}</p>
      <p class="notice-text">{{ $t("proofreadingSelectManuscriptPage.aiNoticeNoLearning") }}</p>
      <p class="notice-text">{{ $t("proofreadingSelectManuscriptPage.aiNoticeCharCount") }}</p>
      <p class="notice-text">{{ $t("proofreadingSelectManuscriptPage.aiNoticeBasicUnavailable") }}</p>
      <p class="notice-text">{{ $t("proofreadingSelectManuscriptPage.aiNoticeNovelOnly") }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { Manuscript } from "@/lib/models";
import ExportManuscriptListSelector from "@/components/ui/exportManuscriptDialogs/ExportManuscriptListSelector.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { useProofreadingBasicStore } from "@/stores/proofreadingBasic";
import { useProofreadingAiStore } from "@/stores/proofreadingAi";
import { AI_PROOFREADING_MAX_CHARS } from "@/constants/proofreadingAiChecks";
import { calcAiProofreadingFishEstimate } from "@/lib/utils/proofreadingAiFish";
import {
  loadManuscriptCharacterCounts,
  sumCharacterCounts,
} from "@/lib/utils/proofreadingManuscriptChars";
import { ensureManuscriptsLoaded } from "@/lib/utils/manuscriptLoader";

export default Vue.extend({
  components: {
    ExportManuscriptListSelector,
    LoadingSpinner,
  },
  props: {
    novelId: String,
  },
  data() {
    return {
      proofreadingBasicStore: useProofreadingBasicStore(),
      proofreadingAiStore: useProofreadingAiStore(),
      selectedManuscripts: [] as Manuscript[],
      characterCountsByKey: {} as Record<string, number>,
      failedCountKeys: [] as string[],
      isLoadingCounts: false,
      isLoadingManuscripts: true,
    };
  },
  computed: {
    isAiMode(): boolean {
      return this.$route.query.mode === "ai" || this.proofreadingAiStore.isAiMode;
    },
    manuscriptList(): Manuscript[] {
      return (this.$store.getters["manuscriptModule/manuscriptList"] as Manuscript[]) ?? [];
    },
    hasManuscripts(): boolean {
      return this.manuscriptList.length > 0;
    },
    selectedManuscriptKeys(): string[] {
      if (this.isAiMode) {
        return this.proofreadingAiStore.selectedManuscriptKeys;
      }
      return this.proofreadingBasicStore.selectedManuscriptKeys;
    },
    selectedTotalChars(): number {
      const keys = this.selectedManuscripts
        .map((m) => m.key)
        .filter((k): k is string => typeof k === "string" && k.length > 0);
      return sumCharacterCounts(keys, this.characterCountsByKey);
    },
    formattedTotalChars(): string {
      return this.selectedTotalChars.toLocaleString(this.$i18n.locale || "ja");
    },
    isExceededMaxChars(): boolean {
      return this.isAiMode && this.selectedTotalChars > AI_PROOFREADING_MAX_CHARS;
    },
    currentFish(): number {
      return this.$store.getters["fishModule/currentFish"] as number;
    },
    estimatedFish(): number {
      return calcAiProofreadingFishEstimate(this.selectedTotalChars, 1);
    },
    isFishInsufficient(): boolean {
      if (!this.isAiMode || this.selectedTotalChars <= 0) return false;
      const canConsume = this.$store.getters["fishModule/canConsumeFish"];
      if (typeof canConsume === "function") {
        return !canConsume(this.estimatedFish);
      }
      return this.currentFish < this.estimatedFish;
    },
    fishShortage(): number {
      if (!this.isFishInsufficient) return 0;
      return Math.max(0, this.estimatedFish - this.currentFish);
    },
    formattedFishShortage(): string {
      return this.fishShortage.toLocaleString(this.$i18n.locale || "ja");
    },
    showFishWarning(): boolean {
      return this.isExceededMaxChars || this.isFishInsufficient;
    },
    fishWarningMessage(): string {
      if (this.isExceededMaxChars) {
        return this.$t("proofreadingSelectManuscriptPage.exceededMaxChars") as string;
      }
      if (this.isFishInsufficient) {
        return this.$t("proofreadingSelectManuscriptPage.fishInsufficient") as string;
      }
      return "";
    },
    hasCountLoadFailure(): boolean {
      return this.isAiMode && this.failedCountKeys.length > 0;
    },
    canProceed(): boolean {
      if (this.isLoadingManuscripts || this.isLoadingCounts || this.selectedManuscripts.length === 0) {
        return false;
      }
      if (!this.isAiMode) {
        return true;
      }
      if (this.hasCountLoadFailure) return false;
      if (this.selectedTotalChars <= 0) return false;
      if (this.isExceededMaxChars || this.isFishInsufficient) return false;
      return true;
    },
  },
  watch: {
    selectedManuscripts: {
      handler(manuscripts: Manuscript[]) {
        if (!this.isAiMode) return;
        const keys = manuscripts
          .map((m) => m.key)
          .filter((k): k is string => typeof k === "string" && k.length > 0);
        this.proofreadingAiStore.setSelectedManuscriptKeys(keys);
        this.proofreadingAiStore.setTotalChars(this.selectedTotalChars);
      },
      deep: true,
    },
  },
  async mounted() {
    if (this.novelId) {
      try {
        await ensureManuscriptsLoaded(this.$store, this.novelId);
      } finally {
        this.isLoadingManuscripts = false;
      }
    } else {
      this.isLoadingManuscripts = false;
    }

    if (this.isAiMode) {
      this.proofreadingAiStore.setMode("ai");
      if (this.novelId) {
        this.proofreadingAiStore.setActiveNovelId(this.novelId);
      }
      this.$store.dispatch("fishModule/fetchFishData");
      await this.loadCharacterCounts();
    }

    this.restoreSelectedManuscripts();
  },
  methods: {
    async loadCharacterCounts() {
      if (!this.novelId || this.manuscriptList.length === 0) {
        this.isLoadingCounts = false;
        return;
      }

      this.isLoadingCounts = true;
      try {
        const { counts, failedKeys } = await loadManuscriptCharacterCounts(
          this.novelId,
          this.manuscriptList,
        );
        this.characterCountsByKey = counts;
        this.failedCountKeys = failedKeys;
        this.proofreadingAiStore.setManuscriptCharCounts(counts);
      } finally {
        this.isLoadingCounts = false;
      }
    },
    restoreSelectedManuscripts() {
      if (this.selectedManuscriptKeys.length > 0 && this.manuscriptList.length > 0) {
        const keyToManuscript = new Map(
          this.manuscriptList.map((m) => [m.key, m] as const),
        );
        this.selectedManuscripts = this.selectedManuscriptKeys
          .map((k) => keyToManuscript.get(k))
          .filter((m): m is Manuscript => m != null);
      }
    },
    onClickNext() {
      if (!this.canProceed || !this.novelId) {
        return;
      }
      const keys = this.selectedManuscripts
        .map((m) => m.key)
        .filter((k): k is string => typeof k === "string" && k.length > 0);

      if (this.isAiMode) {
        this.proofreadingAiStore.setSelectedManuscriptKeys(keys);
        this.proofreadingAiStore.setTotalChars(this.selectedTotalChars);
        this.$router.push({
          name: "proofreadingSelectChecks",
          params: { novelId: this.novelId },
        });
        return;
      }

      this.proofreadingBasicStore.setSelectedManuscriptKeys(keys);
      this.$router.push({
        name: "proofreadingReviseManuscript",
        params: { novelId: this.novelId },
      });
    },
    onClickBack() {
      if (!this.novelId) return;
      if (this.isAiMode) {
        this.$router.push({
          name: "proofreadingPreviewPlan",
          params: { novelId: this.novelId },
        });
        return;
      }
      this.$router.push({
        name: "proofreadingSelectType",
        params: { novelId: this.novelId },
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-select-manuscript {
  display: flex;
  flex-direction: column;
  max-width: 640px;
  min-height: 100%;
  margin: 0 auto;
  padding: 40px 24px;
  color: var(--color-text-normal);
}

.proofreading-select-manuscript__heading {
  margin: 0 0 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  text-align: left;
  color: var(--color-text-normal);
}

.proofreading-select-manuscript__loading {
  display: flex;
  justify-content: center;
  padding: 32px 0;
}

.proofreading-select-manuscript__empty {
  margin: 32px 0;
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--color-text-low);
}

.proofreading-select-manuscript__selector {
  display: flex;
  flex-direction: column;
}

.proofreading-select-manuscript__fish-info {
  margin-top: 16px;
  text-align: right;
}

.fish-info-content {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

.fish-info-amount {
  color: var(--color-text-normal);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}

.fish-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background-color: var(--color-warning-bg);
  border: 1px solid var(--color-warning-border);
  border-radius: var(--radius-xs);
  text-align: left;
}

.fish-warning-icon {
  flex-shrink: 0;
}

.fish-warning-message {
  margin: 0 0 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-warning-text);
}

.fish-warning-detail {
  margin: 0;
  font-size: var(--font-size-xs);
  color: var(--color-warning-text);
}

.fish-shortage-amount {
  font-weight: var(--font-weight-bold);
}

.proofreading-select-manuscript__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.proofreading-select-manuscript__back-button {
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 32px;
  background-color: #ebebeb;
  color: var(--color-gray-1);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
}

.proofreading-select-manuscript__next-button {
  margin-left: auto;
  border: none;
  border-radius: var(--radius-rounded);
  padding: 12px 48px;
  background-color: var(--color-primary);
  color: var(--color-text-invert);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.proofreading-select-manuscript__notice {
  margin-top: 36px;
  padding-top: 4px;
}

.notice-text {
  margin: 0 0 8px;
  font-size: var(--font-size-sm);
  line-height: 1.6;
  color: var(--color-text-normal);

  &:last-child {
    margin-bottom: 0;
  }
}

@include mq("sp") {
  .proofreading-select-manuscript {
    padding: 24px 16px;
  }
}
</style>
